import { apiRequest } from './request'
import type { ApiResponse } from './types'

// ============================================================================
// 类型定义
// ============================================================================

/**
 * 基础菜单数据接口
 */
export interface BaseMenuData {
  name: string
  path: string
  icon?: string
  iconColor?: string
  visible: boolean
  parentId: number
  permissionCode?: string
  sort?: number
}

/**
 * 创建菜单请求接口
 */
export interface CreateMenuRequest extends BaseMenuData {}

/**
 * 更新菜单请求（继承基础属性，不包含ID和时间戳）
 */
export interface UpdateMenuRequest extends BaseMenuData {}

/**
 * 更新菜单排序请求
 */
export interface UpdateMenuOrderRequest {
  menuId: number
  sort: number
}

/**
 * 批量更新菜单排序请求（用于拖拽排序）
 */
export interface BatchUpdateMenuOrderRequest {
  menuOrders: UpdateMenuOrderRequest[]
}



/**
 * 菜单项接口（包含ID和时间戳）
 */
export interface MenuItem extends BaseMenuData {
  id: number
  createdAt: string
  updatedAt?: string
  sort?: number
  children?: MenuItem[]
  updating?: boolean // 用于跟踪更新状态
  level?: number // 层级深度
  hasChildren?: boolean // 是否有子菜单
  expanded?: boolean // 是否展开
}

/**
 * 菜单树项接口（用于树形结构）
 */
export interface MenuTreeItem {
  id: number
  parentId: number
  name: string
  path: string
  icon?: string
  iconColor?: string
  visible: boolean
  permissionCode?: string
  sort?: number
  hasPermission?: boolean
  children?: MenuTreeItem[]
}

/**
 * 权限树节点类型
 */
export interface PermissionTreeNode {
  name: string
  code: string
  type: string
  permissionCode?: string
  permissionName?: string
  permissionType?: string
  apiPath?: string
  httpMethod?: string
  children?: PermissionTreeNode[]
}

// ============================================================================
// 菜单管理API
// ============================================================================

export const menuApi = {
  // 获取品牌全部菜单树
  getBrandMenuTree: (brandId: number): Promise<ApiResponse<MenuTreeItem[]>> => {
    return apiRequest.get(`/api/BusinessMenu/brand/${brandId}/tree`)
  },

  // 获取当前用户可访问的菜单树（后端会自动从JWT Token中获取品牌信息）
  getMyMenus: (): Promise<ApiResponse<MenuTreeItem[]>> => {
    return apiRequest.get('/api/BusinessMenu/my-menus')
  },

  // 获取指定用户的菜单树
  getUserMenus: (userId: number, brandId: number): Promise<ApiResponse<MenuTreeItem[]>> => {
    return apiRequest.get(`/api/BusinessMenu/user/${userId}/menus?brandId=${brandId}`)
  },

  // 获取权限树（保留原有接口，如果需要的话）
  getPermissionTree: (): Promise<ApiResponse<PermissionTreeNode[]>> => {
    return apiRequest.get('/api/Permission/tree')
  },

  // 为了兼容性，保留原有的getMenuTree方法，现在直接调用用户菜单
  getMenuTree: (): Promise<ApiResponse<MenuTreeItem[]>> => {
    return apiRequest.get('/api/BusinessMenu/my-menus')
  },

  // 以下方法暂时保留，但可能需要根据实际需求调整
  // 获取菜单详情
  getMenuDetail: (id: number): Promise<ApiResponse<MenuItem>> => {
    return apiRequest.get(`/api/BusinessMenu/${id}`)
  },

  // 创建菜单
  createMenu: (data: CreateMenuRequest): Promise<ApiResponse<MenuItem>> => {
    return apiRequest.post('/api/BusinessMenu', data)
  },

  // 更新菜单
  updateMenu: (id: number, data: UpdateMenuRequest): Promise<ApiResponse<boolean>> => {
    return apiRequest.put(`/api/BusinessMenu/${id}`, data)
  },

  // 删除菜单
  deleteMenu: (id: number): Promise<ApiResponse<boolean>> => {
    return apiRequest.delete(`/api/BusinessMenu/${id}`)
  },

  // 批量删除菜单
  batchDeleteMenu: (ids: number[]): Promise<ApiResponse<boolean>> => {
    return apiRequest.post('/api/BusinessMenu/batch-delete', { ids })
  },

  // 更新菜单显示状态
  updateMenuVisibility: (id: number, visible: boolean): Promise<ApiResponse<boolean>> => {
    return apiRequest.put(`/api/BusinessMenu/${id}/visibility`, { visible })
  },

  // 批量更新菜单排序（用于拖拽排序）
  batchUpdateMenuSort: (request: BatchUpdateMenuOrderRequest): Promise<ApiResponse<boolean>> => {
    return apiRequest.put('/api/BusinessMenu/batch-sort', request)
  },


} 